/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/naming-convention */
/** @jsxImportSource solid-js */
import type { Component, JSX } from 'solid-js';
import { createEffect, createSignal } from 'solid-js';
import { noShadowDOM } from 'solid-element';

export interface CustomCheckboxProps {
  name: string;
  value: string;

  // Common input attributes
  id?: string;
  checked?: boolean;
  disabled?: boolean;
  required?: boolean;
  'data-cy'?: string; // support direct attribute passthrough
  dataCy?: string; // alternative prop name that maps to data-cy

  // Event callbacks (optional)
  onChange?(
    event: Event & { currentTarget: HTMLInputElement; target: Element }
  ): void;
  onInput?(
    event: InputEvent & { currentTarget: HTMLInputElement; target: Element }
  ): void;
  onBlur?(
    event: FocusEvent & { currentTarget: HTMLInputElement; target: Element }
  ): void;
  onFocus?(
    event: FocusEvent & { currentTarget: HTMLInputElement; target: Element }
  ): void;

  // Callback to expose input ref for imperative access
  onInputRef?(ref: HTMLInputElement | undefined): void;
}

const CustomCheckbox: Component<CustomCheckboxProps> = (props): JSX.Element => {
  noShadowDOM();
  let inputRef: HTMLInputElement | undefined;
  const [innerChecked, setInnerChecked] = createSignal<boolean>(false);

  // Expose input ref for imperative access
  createEffect(() => {
    props.onInputRef?.(inputRef);
  });

  // Reactive getters for props
  const getName = () => props.name || '';
  const getValue = () => props.value || '';
  const getId = () => props.id || `${getName()}-${getValue()}`;
  const getChecked = () => props.checked ?? false;
  const getDisabled = () => props.disabled ?? false;
  const getRequired = () => props.required ?? false;
  const getDataCy = () => props['data-cy'] || props.dataCy || getName();

  // Sync with checked prop
  createEffect(() => {
    setInnerChecked(getChecked());
  });

  const handleChange = (
    event: Event & { currentTarget: HTMLInputElement; target: Element }
  ) => {
    const target = event.currentTarget;
    const isChecked = target.checked;

    setInnerChecked(isChecked);

    // Call the onChange callback if provided
    props.onChange?.(event);
  };

  const handleInput = (
    event: InputEvent & { currentTarget: HTMLInputElement; target: Element }
  ) => {
    props.onInput?.(event);
  };

  const handleBlur = (
    event: FocusEvent & { currentTarget: HTMLInputElement; target: Element }
  ) => {
    props.onBlur?.(event);
  };

  const handleFocus = (
    event: FocusEvent & { currentTarget: HTMLInputElement; target: Element }
  ) => {
    props.onFocus?.(event);
  };

  return (
    <div class="icon checkbox">
      <input
        ref={inputRef}
        id={getId()}
        class="custom-input"
        data-cy={getDataCy()}
        type="checkbox"
        name={getName()}
        value={getValue()}
        checked={innerChecked()}
        disabled={getDisabled()}
        required={getRequired()}
        onChange={handleChange}
        onInput={handleInput}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />
      <label for={getId()} />
    </div>
  );
};

export default CustomCheckbox;
